<script setup>
// The conversation's side panel: the kit's Inspector with the tabs Details (the contact panel) and Ask
// Polaris (the Copilot). Mounted while a conversation is open; it draws nothing while the panel is closed
// (the conversation header then shows the button that opens it).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
import Inspector from 'dashboard/components-next/polaris/cards/Inspector.vue';
import {
  useConversationPanel,
  useMountConversationPanel,
  focusPanelTab,
  focusPanelOpener,
  useOverlayEscape,
  ASK_OPENER_SELECTOR,
} from 'dashboard/composables/useConversationPanel';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { t } = useI18n();
const { width: windowWidth } = useWindowSize();
const { hasAskTab, isOpen, activeTab, setTab, close, toggleDetails } =
  useConversationPanel();

useMountConversationPanel();

const onToggleShortcut = () => {
  toggleDetails();
  if (isOpen.value) focusPanelTab();
  else focusPanelOpener();
};
useKeyboardEvents({ 'Alt+KeyO': { action: onToggleShortcut } });

const tabs = computed(() => [
  { value: 'details', label: t('CONVERSATION.SIDEBAR.DETAILS') },
  ...(hasAskTab.value
    ? [{ value: 'ask', label: t('CONVERSATION.SIDEBAR.COPILOT') }]
    : []),
]);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.OVERLAY_PANEL_BREAKPOINT
);

// Below xl the panel lies over the conversation; a click outside it closes it, as both old panels did.
const closeOnSmallScreen = () => {
  if (isSmallScreen.value && isOpen.value) close();
};

const onOpenChange = value => {
  if (value) return;
  close();
  focusPanelOpener();
};

// Escape closes the overlay too (✕ and a click outside already did); the column from xl ignores it, as before.
const panelRef = ref(null);
useOverlayEscape({
  isOverlayOpen: computed(() => isSmallScreen.value && isOpen.value),
  panel: panelRef,
  close,
  fallbackOpener: '[data-conversation-panel-opener]',
});
</script>

<template>
  <div
    v-if="isOpen"
    ref="panelRef"
    v-on-click-outside="[
      closeOnSmallScreen,
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
          // The kit's popovers (the note delete confirm) are teleported to the body inside this wrapper.
          '[data-reka-popper-content-wrapper]',
          // The top bar's Ask Polaris toggles the Ask tab itself.
          ASK_OPENER_SELECTOR,
        ],
      },
    ]"
    data-conversation-panel
    class="fixed bottom-0 top-14 z-40 flex w-full max-w-sm flex-col shadow-lg ltr:right-0 rtl:left-0 md:w-[320px] md:min-w-[320px] xl:static xl:h-full xl:shadow-none 2xl:w-[360px] 2xl:min-w-[360px]"
  >
    <!-- Below xl (1280px) the panel opens over the conversation, under the Topbar at the inline end, as the woot's panel
         always did (not the kit's modal Sheet: its focus trap and pointer lock would block the woot's own
         dialogs and dropdowns, teleported to the body, opened from inside the panel). -->
    <Inspector
      open
      :tab="activeTab"
      :tabs="tabs"
      :label="t('CONVERSATION.SIDEBAR.PANEL')"
      class="h-full w-full rtl:border-l-0 rtl:border-r"
      @update:tab="setTab"
      @update:open="onOpenChange"
    >
      <template #details>
        <ContactPanel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </template>
      <template #ask>
        <CopilotContainer embedded />
      </template>
    </Inspector>
  </div>
  <template v-else />
</template>
